"use client"
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Container, Row, Col } from "react-bootstrap";
import MapBoxMapWrapper from "@/components/shared/MapBoxMapWrapper";

import { useMediaQuery } from "react-responsive";
import useUtils from "@/components/shared/useUtils";
import useSystemLayers from "@/components/shared/map/useSystemLayers";
import useApi from "@/components/api/useApi";

import CommunityDirectoryMemberView from "@/components/communities/CommunityDirectoryMemberView";

import "@/components/communities/CommunityDirectoryMapAndPins.css";
import CommunityDirectoryPointFilterv2 from "@/components/communities/CommunityDirectoryPointFilterv2";

const CommunityDirectoryMapAndPins = ({
  currentLocation,
  showMap,
  community,
  points,
  types,
  external,
  scrollable,
  showCoverPhoto = false,
  showSearchFilter = true,
  showOpenNow = true,
  name,
  systemLayerTypes,
  token
}) => {
  // fallback when the community's map pin styles can't be loaded
  const defaultMapLayers = [
    {
      value: "AMBASSADOR",
      label: "Ambassadors",
      layerMarkerType: "VARIANT",
      layerMarkerRef: "2DC5F5"
    },
    {
      value: "POI",
      label: "Points of Interest",
      layerMarkerType: "VARIANT",
      layerMarkerRef: "EBCD14"
    },
    {
      value: "BUSINESS",
      label: "Business",
      layerMarkerType: "VARIANT",
      layerMarkerRef: "1565C0"
    }
  ];

  const api = useApi();
  // resolved pin styles (defaults + community overrides); null until loaded, false if unavailable
  const [pinStyles, setPinStyles] = useState(null);

  useEffect(() => {
    if (!showMap) {
      setPinStyles(false);
      return;
    }
    if (name) {
      api
        .getCommunityMapConfiguration(name, token)
        .then(config => setPinStyles(config))
        .catch(error => {
          console.log("Unable to load map pin styles", error);
          setPinStyles(false);
        });
    } else {
      setPinStyles(false);
    }
  }, [name, token, showMap]);

  // same precedence as the server's PinStyleResolver: the entries already carry effective values
  const pinStyleFor = userProfile => {
    if (!pinStyles || !userProfile) return null;
    const macroEntry = userProfile.macroType
      ? pinStyles.macroTypes.find(e => e.macroType === userProfile.macroType)
      : null;
    const entry =
      macroEntry ??
      pinStyles.profileTypes.find(
        e => e.userProfileType === userProfile.userProfileType
      );
    if (!entry?.color) return null;
    return {
      layer: `PIN.${entry.color.replace("#", "")}/${entry.icon ?? ""}`,
      color: entry.color.replace("#", ""),
      icon: entry.icon,
      label: entry.label
    };
  };

  const [locationList, setLocationList] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);

  const [progressTick, setProgressTick] = useState(0);
  const [mapPoints, setMapPoints] = useState(null);
  const [filteredPoints, setFilteredPoints] = useState(null);
  const [filteredLocations, setFilteredLocations] = useState(null);
  const [horizontalOffset, setHorizontalOffset] = useState(0);

  const mapLayers = useMemo(() => {
    if (!pinStyles || !points) return defaultMapLayers;
    const layersByValue = new Map();
    points.forEach(point => {
      const style = pinStyleFor(point.userProfile);
      if (style && !layersByValue.has(style.layer)) {
        layersByValue.set(style.layer, {
          value: style.layer,
          label: style.label,
          layerMarkerType: "VARIANT",
          layerMarkerRef: style.color,
          icon: style.icon,
          shape: "CIRCLE"
        });
      }
    });
    return [...defaultMapLayers, ...layersByValue.values()];
  }, [pinStyles, points]);

  const isSm = useMediaQuery({ maxWidth: 767.98 });
  const utils = useUtils();
  const systemLayers = useSystemLayers(name, systemLayerTypes, token);

  const mapDefaultCenter = useMemo(() => {
    const bounds = utils.computeBoundingBoxFromPoints(locationList ?? []);
    if (!bounds) return null;
    return { fitBounds: bounds, fitBoundsOptions: { padding: 40, maxZoom: 15 } };
  }, [locationList]);

  const observer = useRef();
  const handleScroll = useRef(true);
  const scrollableRef = useRef();

  const theDirectory = useRef();

  useEffect(() => {
    if (!isSm && showMap) {
      setHorizontalOffset(screen.width * 0.25);
    }
  }, []);

  useEffect(() => {
    if (mapPoints && showMap) {
      const handleIntersection = (entries, obs) => {
        var stop = false;
        if (handleScroll.current) {
          entries.forEach(entry => {
            if (entry.isIntersecting && !stop) {
              var point = mapPoints.find(pt => pt.id === entry.target.id);
              if (point) {
                stop = true;
                onMarkerFocused(point);
              }
            }
          });
        }
      };

      // For desktop with-map, use the scrollable column as the root so that
      // intersection is measured relative to the visible scroll container,
      // not the browser viewport (which would be confused by overflow:hidden).
      const scrollRoot =
        !isSm && showMap ? (scrollableRef.current ?? null) : null;

      const observerOptions = {
        root: scrollRoot,
        rootMargin: isSm ? "30px 0px -95% 0px" : "40px 0px -95% 0px",
        threshold: 0
      };
      observer.current = new IntersectionObserver(
        handleIntersection,
        observerOptions
      );

      // Scope the query to the scroll container when available so we don't
      // accidentally observe member-view elements from other components.
      const scope = scrollRoot ?? document;
      const entries = scope.querySelectorAll(
        "div.community-directory-member-view"
      );
      if (entries) {
        entries.forEach(entry => {
          observer.current.observe(entry);
        });
      }

      return () => {
        observer.current?.disconnect();
        observer.current = null;
      };
    }
  }, [mapPoints]);

  useEffect(() => {
    // wait for pin styles so the map's layers are complete when it first loads
    if (points && points.length > 0 && pinStyles !== null) {
      if (showMap) {
        setLocationList(createLocationList(points));
      }
      let newMapPoints = [];
      points.forEach(mapPoint => {
        mapPoint.userProfile["location"] = mapPoint.location;
        newMapPoints.push(mapPoint);
      });
      setMapPoints(newMapPoints);
      setFilteredPoints(newMapPoints);
    }
  }, [points, pinStyles]);

  useEffect(() => {
    setTimeout(() => {
      sendResizeMessage();
    }, 250);
  }, [filteredPoints]);

  const sendResizeMessage = () => {
    if (
      external &&
      !scrollable &&
      !showMap &&
      theDirectory &&
      theDirectory.current
    ) {
      let message = {
        type: "resize",
        height: theDirectory.current.scrollHeight,
        width: theDirectory.current.scrollWidth
      };
      // window.top refers to parent window
      // console.log("posting message to parent", message);
      window.top.postMessage(message, "*");
    }
  };

  const createLocationList = points => {
    var locationList = [];
    points.forEach(function(point) {
      if (point.location) {
        var locationInfo = {};
        locationInfo["latitude"] = point.location.latitude;
        locationInfo["longitude"] = point.location.longitude;
        locationInfo["id"] = point.id;
        locationInfo["markerImageId"] = null; //point.layer;
        locationInfo["layer"] =
          pinStyleFor(point.userProfile)?.layer ??
          point.userProfile.userProfileType;
        locationInfo["label"] = point.userProfile.displayName;
        locationList.push(locationInfo);
      }
    });
    return locationList;
  };

  const onLocationClicked = location => {
    // console.log("MapAndPins onLocationClicked", location);
    handleScroll.current = false;
    setSelectedMarker(location);
    var pointIndex = mapPoints.findIndex(pt => pt.id === location.id);
    if (pointIndex > -1) {
      setProgressTick(pointIndex);
    }
    var el = document.getElementById("anchor-" + location.id);
    if (el != null) {
      el.scrollIntoView({ block: "center" });
      el.scrollIntoView({ behavior: "smooth" });
      // el.classList.add('selected');
    }
    setTimeout(() => {
      handleScroll.current = true;
    }, 2000);
  };

  const onMarkerFocused = point => {
    // console.log("MapAndPins onMarkerFocused", point, handleScroll);
    if (handleScroll.current) {
      if (locationList) {
        let location = locationList.find(loc => loc.id === point.id);
        if (location != null) {
          setSelectedMarker(location);
        }
        var pointIndex = mapPoints.findIndex(pt => pt.id === point.id);
        if (pointIndex > -1) {
          setProgressTick(pointIndex);
        }
      }
    }
  };

  const onCardClicked = (evt, point) => {
    // console.log("MapAndPins onCardClicked", point);
    handleScroll.current = false;

    evt.preventDefault();
    evt.stopPropagation();

    var el = document.getElementById("anchor-" + point.id);
    if (el != null) {
      el.scrollIntoView({ behavior: "smooth" });
      // el.classList.add('selected');
    }

    if (locationList) {
      let location = locationList.find(loc => loc.id === point.id);
      if (location != null) {
        if (selectedMarker !== location) {
          setSelectedMarker(location);
        }
      }

      var pointIndex = mapPoints.findIndex(pt => pt.id === point.id);
      if (pointIndex > -1) {
        setProgressTick(pointIndex);
      }
    }
    setTimeout(() => {
      handleScroll.current = true;
    }, 500);
  };

  const theMap = () => {
    return (
      <>
        <div className={`the-map${external ? " embedded" : ""}`}>
          <MapBoxMapWrapper
            addClass={isSm ? "mapbox-mobile-map" : ""}
            height={
              isSm ? "240px" : "100%" // TODO adjust for filter
            }
            width="100%"
            horizontalOffset={horizontalOffset}
            locations={filteredLocations ?? locationList ?? []}
            layers={mapLayers}
            selectedLocation={selectedMarker}
            onLocationClicked={onLocationClicked}
            onMapClicked={() => setSelectedMarker(null)}
            defaultZoom={15}
            defaultCenter={mapDefaultCenter}
            systemLayers={systemLayers}
          />
        </div>
      </>
    );
  };

  const theLocations = filteredLocations ?? locationList;

  const onFilterSelect = newFilteredPoints => {
    if (newFilteredPoints) {
      setFilteredPoints(newFilteredPoints);
      if (showMap) {
        setFilteredLocations(createLocationList(newFilteredPoints));
        setSelectedMarker(null);
      }
    } else {
      setFilteredPoints(null);
      if (showMap) {
        setFilteredLocations(null);
        setSelectedMarker(null);
      }
    }
  };

  if ((mapPoints && theLocations && showMap) || (mapPoints && !showMap)) {
    return (
      <div
        ref={theDirectory}
        className={`community-directory-map-and-pins ${
          scrollable ? "scrollable" : ""
        }`}
      >
        {showSearchFilter && (
          <div className="community-directory-filter">
            <CommunityDirectoryPointFilterv2
              community={community}
              points={mapPoints}
              types={types}
              basic={false}
              showOpenNow={showOpenNow}
              onSelect={onFilterSelect}
            />
          </div>
        )}

        <div className="community-directory-content">
          {isSm && (
            <>
              {showMap && theMap()}
              {!showMap && filteredPoints && filteredPoints.length === 0 && (
                <Container>
                  <Row>
                    <Col className="col-12">
                      <div
                        className="center-child-div-parent"
                        style={{ paddingTop: "4rem" }}
                      >
                        <p style={{ fontSize: "1.125rem" }}>
                          No results to display
                        </p>
                      </div>
                    </Col>
                  </Row>
                </Container>
              )}
              <Container>
                {mapPoints.map(function(mapPoint, index) {
                  var className = filteredPoints
                    ? filteredPoints.findIndex(
                        pt => pt.id === mapPoint.id
                      ) === -1
                      ? "hide"
                      : ""
                    : "";
                  return (
                    <Row key={mapPoint.id}>
                      <Col>
                        <CommunityDirectoryMemberView
                          id={mapPoint.id}
                          className={`default-card-wrapper ${className}`}
                          community={community}
                          profile={mapPoint.userProfile}
                          coordinates={currentLocation}
                          external={external}
                          wrapperClassName={
                            showMap
                              ? index === progressTick
                                ? "selected"
                                : ""
                              : ""
                          }
                          onClick={
                            showMap ? evt => onCardClicked(evt, mapPoint) : null
                          }
                          showCoverPhoto={showCoverPhoto}
                        />
                      </Col>
                    </Row>
                  );
                })}
              </Container>
            </>
          )}

          {!isSm && (
            <>
              {!showMap &&filteredPoints && filteredPoints.length === 0 ? (
                <div
                  className="center-child-div-parent"
                  style={{ paddingTop: "4rem" }}
                >
                  <p style={{ fontSize: "1.125rem" }}>No results to display</p>
                </div>
              ) : (
                <>
                  {showMap ? (
                    <>
                      <div className="pins with-map">
                        <div className="scrollable-div" ref={scrollableRef}>
                          {mapPoints.map(function(mapPoint, index) {
                            var className = filteredPoints
                              ? filteredPoints.findIndex(
                                  pt => pt.id === mapPoint.id
                                ) === -1
                                ? "hide"
                                : ""
                              : "";
                            return (
                              <CommunityDirectoryMemberView
                                key={mapPoint.id}
                                id={mapPoint.id}
                                className={`default-card-wrapper ${className}`}
                                community={community}
                                profile={mapPoint.userProfile}
                                coordinates={currentLocation}
                                external={external}
                                wrapperClassName={
                                  index === progressTick ? "selected" : ""
                                }
                                onClick={
                                  showMap
                                    ? evt => onCardClicked(evt, mapPoint)
                                    : null
                                }
                                showCoverPhoto={showCoverPhoto}
                              />
                            );
                          })}
                        </div>
                      </div>
                      {theMap()}
                    </>
                  ) : (
                    <div className="pins">
                      {mapPoints.map(function(mapPoint, index) {
                        var className = filteredPoints
                          ? filteredPoints.findIndex(
                              pt => pt.id === mapPoint.id
                            ) === -1
                            ? "hide"
                            : ""
                          : "";
                        return (
                          <CommunityDirectoryMemberView
                            key={mapPoint.id}
                            id={mapPoint.id}
                            className={className}
                            community={community}
                            profile={mapPoint.userProfile}
                            coordinates={currentLocation}
                            external={external}
                            onClick={
                              showMap
                                ? evt => onCardClicked(evt, mapPoint)
                                : null
                            }
                            showCoverPhoto={showCoverPhoto}
                          />
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  } else {
    return <></>;
  }
};
export default CommunityDirectoryMapAndPins;
